import { API_ENDPOINTS } from '@/services/api/endpoints';
import { clearTokens, getRefreshToken, setAccessToken } from './token-store';

/**
 * In-flight refresh, shared by every caller.
 *
 * When an access token lapses, several requests typically fail at once (the
 * page, a poll, a socket reconnect). Without this, each would fire its own
 * refresh and the extra round trips would race. All callers await the same
 * promise instead.
 */
let inFlight: Promise<string | null> | null = null;

/**
 * Exchanges the stored refresh token for a new access token.
 *
 * Concurrent calls share one network request. On definitive failure the tokens
 * are cleared so the caller can fall through to a real logout; on a network
 * blip the tokens are left alone so the user can retry.
 *
 * @returns The new access token, or `null` if the session could not be renewed.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) {
    return inFlight;
  }

  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  let response: Response;
  try {
    response = await fetch(API_ENDPOINTS.AUTH.REFRESH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Offline or the API is unreachable. The session may still be perfectly
    // valid, so keep the tokens and let the next attempt decide.
    return null;
  }

  if (!response.ok) {
    // 401 means the refresh token itself is dead (revoked, expired, or the
    // account was blocked) — there is nothing left to recover.
    if (response.status === 401) {
      clearTokens();
    }
    return null;
  }

  try {
    const body = (await response.json()) as { result?: { token?: string } };
    const token = body?.result?.token;
    if (!token) {
      return null;
    }

    setAccessToken(token);
    return token;
  } catch {
    return null;
  }
}

/** Test seam — drops any shared in-flight refresh. */
export function resetRefreshState(): void {
  inFlight = null;
}
